/**
 * #1198 — White-label brand pack for OEM tenants.
 *
 * Default = Foam CaseBuilder production brand. When `VITE_WHITELABEL_ENABLED=true`,
 * logo / product name / primary color / canonical origin come from Vite env so
 * a deploy can ship a branded skin without forking the app.
 *
 * Tenant isolation of designs/API is a separate server concern — this module
 * only covers presentation brand surface.
 */

export type WhiteLabelBrand = {
  enabled: boolean;
  productName: string;
  canonicalOrigin: string;
  logoUrl: string;
  primaryColor: string;
  /** Hover/active primary — explicit env or derived darker mix of `primaryColor`. */
  primaryDarkColor: string;
};

export const DEFAULT_WHITELABEL_BRAND: Readonly<WhiteLabelBrand> = {
  enabled: false,
  productName: 'Foam CaseBuilder',
  canonicalOrigin: 'https://foamcasebuilder.com',
  logoUrl: '/favicon.svg',
  primaryColor: '#0b3d5c',
  primaryDarkColor: '#082c42',
};

/** Darker shade for hover/active when OEM only sets one primary. */
export function derivePrimaryDarkColor(primaryColor: string): string {
  return `color-mix(in srgb, ${primaryColor} 80%, black)`;
}

/**
 * Accept #rgb/#rrggbb/#rrggbbaa, rgb()/hsl()/oklch(), or color-mix().
 * Reject junk so a mistyped env cannot write garbage into CSS vars.
 */
export function isCssColor(value: string): boolean {
  const v = value.trim();
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v)) return true;
  if (/^(rgb|hsl|oklch)a?\(/i.test(v)) return true;
  if (/^color-mix\(/i.test(v)) return true;
  return false;
}

function safeColor(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  return isCssColor(value) ? value.trim() : fallback;
}

type EnvLike = Record<string, string | boolean | undefined>;

function str(env: EnvLike, key: string): string | undefined {
  const v = env[key];
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t.length ? t : undefined;
}

/** Pure resolver — pass a mock `env` in tests; production uses `import.meta.env`. */
export function resolveWhiteLabelBrand(env: EnvLike = import.meta.env as EnvLike): WhiteLabelBrand {
  const enabled = String(env.VITE_WHITELABEL_ENABLED ?? '').toLowerCase() === 'true';
  if (!enabled) {
    return { ...DEFAULT_WHITELABEL_BRAND };
  }

  const productName = str(env, 'VITE_WHITELABEL_PRODUCT_NAME') ?? DEFAULT_WHITELABEL_BRAND.productName;
  const canonicalOrigin =
    str(env, 'VITE_WHITELABEL_ORIGIN') ?? DEFAULT_WHITELABEL_BRAND.canonicalOrigin;
  const logoUrl = str(env, 'VITE_WHITELABEL_LOGO_URL') ?? DEFAULT_WHITELABEL_BRAND.logoUrl;
  const primaryColor = safeColor(
    str(env, 'VITE_WHITELABEL_PRIMARY_COLOR'),
    DEFAULT_WHITELABEL_BRAND.primaryColor,
  );
  const explicitDark = str(env, 'VITE_WHITELABEL_PRIMARY_DARK_COLOR');
  const primaryDarkColor = explicitDark
    ? safeColor(explicitDark, derivePrimaryDarkColor(primaryColor))
    : derivePrimaryDarkColor(primaryColor);

  return {
    enabled: true,
    productName,
    canonicalOrigin: canonicalOrigin.replace(/\/$/, ''),
    logoUrl,
    primaryColor,
    primaryDarkColor,
  };
}

/** Apply CSS brand tokens once at boot (no-op when white-label is off). */
export function applyWhiteLabelCssVars(brand: WhiteLabelBrand = resolveWhiteLabelBrand()): void {
  if (typeof document === 'undefined') return;
  if (!brand.enabled) return;
  try {
    const root = document.documentElement.style;
    // index.css drives `.btn-primary` from `--color-primary` / `--color-primary-dark`.
    root.setProperty('--color-primary', brand.primaryColor);
    root.setProperty('--color-primary-dark', brand.primaryDarkColor);
    root.setProperty('--wl-primary', brand.primaryColor);
  } catch {
    // Never block boot on brand CSS — Foam defaults remain in index.css.
  }
}
